import React, { useEffect, useMemo, useState, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  Dialog,
  DialogContent,
  CircularProgress
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import KeyIcon from "@mui/icons-material/VpnKey";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";

import NewUser from "./EnterpriseUsers/NewUser";
import GenerateTempKey from "./EnterpriseUsers/GenerateTemporaryKey";
import EditDeleteUsers from "./EnterpriseUsers/EditDeleteUsers";
import { AppContext } from "../../context/AppContext";

const EnterpriseUsers = ({
  canSeeSuperUsers = false,
  canCreateSuperUsers = false
}) => {
  const { accessToken } = useContext(AppContext);

  const [openNewUser, setOpenNewUser] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [snack, setSnack] = useState({ open: false, msg: "", type: "success" });
  const [anchorEl, setAnchorEl] = useState(null);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [openGenerate, setOpenGenerate] = useState(false);
  const [openEditDelete, setOpenEditDelete] = useState(false);
  const [editDeleteMode, setEditDeleteMode] = useState("edit");
  const [limits, setLimits] = useState(null);
  const [newUserFormKey, setNewUserFormKey] = useState(0);
  const [editingLimit, setEditingLimit] = useState(false);
  const [limitInput, setLimitInput] = useState("");
  const [savingLimit, setSavingLimit] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const isPrivileged = localStorage.getItem("is_superuser") === "true";

  const parseJsonSafe = async (res) => {
    try {
      return await res.json();
    } catch {
      return { ok: false, error: "Respuesta inválida del servidor" };
    }
  };

  const authFetch = async (url, options = {}) => {
    const headers = {
      Accept: "application/json",
      ...(options.headers || {}),
      Authorization: `Bearer ${accessToken}`
    };

    return fetch(url, {
      ...options,
      credentials: "omit",
      headers
    });
  };

  const usuariosVisibles = useMemo(() => {
    if (canSeeSuperUsers) return usuarios;
    return usuarios.filter((u) => u.rol !== "SUPER");
  }, [usuarios, canSeeSuperUsers]);

  const usuariosOperativos = useMemo(() => {
    return usuarios.filter((u) => u.rol !== "SUPER");
  }, [usuarios]);

  const loginUrl = useMemo(() => {
    const ruc = limits?.empresa_ruc;
    if (!ruc) return "https://factusysdr.com";
    return `https://demo.factusysdr.com`;
  }, [limits]);

  const cargarUsuarios = async () => {
    try {
      const res = await authFetch("/sistema/listar-usuarios/");
      const data = await parseJsonSafe(res);
      setUsuarios(Array.isArray(data.usuarios) ? data.usuarios : []);
    } catch {
      setUsuarios([]);
    }
  };

  const cargarLimites = async () => {
    try {
      const res = await authFetch("/enterprise/empresa/limits/", {
        method: "GET"
      });
      const data = await parseJsonSafe(res);

      if (res.ok && data.ok) {
        setLimits(data);
        setLimitInput(String(data.limite_usuarios ?? ""));
      } else {
        setLimits(null);
        setLimitInput("");
      }
    } catch {
      setLimits(null);
      setLimitInput("");
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    cargarUsuarios();
    cargarLimites();
  }, [accessToken]);

  const abrirMenuEnvio = (e, u) => {
    if (sendingEmail) return;
    setAnchorEl(e.currentTarget);
    setUsuarioSeleccionado(u);
  };

  const cerrarMenu = () => {
    if (sendingEmail) return;
    setAnchorEl(null);
    setUsuarioSeleccionado(null);
  };

  const rawLimit = Number(limits?.limite_usuarios ?? 0);
  const limiteUsuarios = Math.max(rawLimit - 1, 0);
  const usuariosActuales = usuariosOperativos.length;

  const abrirNuevoUsuario = async () => {
    if (usuariosActuales >= limiteUsuarios) {
      setSnack({
        open: true,
        msg: "Se alcanzó el límite máximo de usuarios permitidos",
        type: "error"
      });
      return;
    }

    setOpenNewUser(true);
  };

  const cerrarNuevoUsuario = () => {
    setOpenNewUser(false);
    setNewUserFormKey((prev) => prev + 1);
  };

  const enviarWhatsApp = (u) => {
    const numero = u?.telefono || "";
    const mensaje = encodeURIComponent(
      `Hola ${u?.nombre || ""},\n\nTe han creado una cuenta de acceso en ${limits?.empresa_nombre || "FACTUSYSDR"}.\n\nCorreo: ${u?.email || ""}\nClave temporal: ${u?.clave_temporal || ""}\n\nPuedes ingresar aquí:\n${loginUrl || "https://factusysdr.com"}`
    );

    window.open(`https://wa.me/593${numero}?text=${mensaje}`, "_blank");
    setSnack({ open: true, msg: "Clave enviada por WhatsApp", type: "info" });
    cerrarMenu();
  };

  const enviarCorreo = async (u) => {
    if (!u || sendingEmail) return;

    setSendingEmail(true);
    setAnchorEl(null);

    try {
      const res = await authFetch("/sistema/enviar-clave-correo/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ id: u.id, login_url: loginUrl || "https://factusysdr.com" })
      });

      const d = await parseJsonSafe(res);

      if (res.ok && d.ok) {
        setSnack({ open: true, msg: d?.msg || "Clave enviada por correo", type: "success" });
      } else {
        setSnack({ open: true, msg: d?.error || "Error al enviar correo", type: "error" });
      }
    } catch {
      setSnack({ open: true, msg: "Error de conexión", type: "error" });
    } finally {
      setSendingEmail(false);
      setUsuarioSeleccionado(null);
    }
  };

  const mostrarRol = (rol) => {
    if (rol === "SUPER") return "Superusuario";
    if (rol === "ADMIN") return "Administrador";
    if (rol === "VENDOR") return "Vendedor";
    return "Usuario";
  };

  const abrirGenerarClave = (u) => {
    setUsuarioSeleccionado(u);
    setOpenGenerate(true);
  };

  const abrirEditar = (u) => {
    setUsuarioSeleccionado(u);
    setEditDeleteMode("edit");
    setOpenEditDelete(true);
  };

  const abrirEliminar = (u) => {
    setUsuarioSeleccionado(u);
    setEditDeleteMode("delete");
    setOpenEditDelete(true);
  };

  const guardarLimiteUsuarios = async () => {
    if (!isPrivileged) return;

    const valor = String(limitInput || "").trim();

    if (!valor) {
      setSnack({
        open: true,
        msg: "Ingresa un límite de usuarios válido",
        type: "error"
      });
      return;
    }

    try {
      setSavingLimit(true);

      const res = await authFetch("/enterprise/empresa/update-user-limit/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          limite_usuarios: valor
        })
      });

      const data = await parseJsonSafe(res);

      if (!res.ok || !data.ok) {
        setSnack({
          open: true,
          msg: data?.error || "No se pudo actualizar el límite de usuarios",
          type: "error"
        });
        return;
      }

      setLimits((prev) => ({
        ...(prev || {}),
        ...data,
        limite_usuarios: data.limite_usuarios
      }));
      setLimitInput(String(data.limite_usuarios ?? ""));
      setEditingLimit(false);
      setSnack({
        open: true,
        msg: "Límite de usuarios actualizado correctamente",
        type: "success"
      });
    } catch {
      setSnack({
        open: true,
        msg: "Error de conexión",
        type: "error"
      });
    } finally {
      setSavingLimit(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: "bold", color: "#1565C0", mb: 2 }}>
        Gestión de Usuarios del Sistema
      </Typography>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 3 }}>
        <Paper
          sx={{
            flex: 1,
            borderRadius: "14px",
            p: 2,
            boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
            border: "1px solid #E3F2FD"
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap: 2,
              flexDirection: { xs: "column", md: "row" }
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#E3F2FD"
                }}
              >
                <GroupsRoundedIcon sx={{ color: "#1565C0" }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: "0.9rem", color: "#607D8B", fontWeight: 600 }}>
                  # de usuarios
                </Typography>
                <Typography sx={{ fontSize: "0.95rem", color: "#1565C0", fontWeight: 700 }}>
                  {usuariosActuales} / {limiteUsuarios}
                </Typography>
              </Box>
            </Box>

            {isPrivileged && (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ width: { xs: "100%", md: "auto" } }}
              >
                <TextField
                  size="small"
                  label="Capacidad"
                  value={editingLimit ? limitInput : rawLimit}
                  onChange={(e) => setLimitInput(e.target.value.replace(/\D/g, ""))}
                  disabled={!editingLimit || savingLimit}
                  sx={{
                    minWidth: { xs: "100%", sm: 140 },
                    "& .MuiInputBase-input": {
                      fontWeight: 700
                    }
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">usuarios</InputAdornment>
                  }}
                />

                {!editingLimit ? (
                  <Button
                    variant="contained"
                    startIcon={<EditRoundedIcon />}
                    onClick={() => {
                      setLimitInput(String(rawLimit || ""));
                      setEditingLimit(true);
                    }}
                    sx={{
                      backgroundColor: "#1565C0",
                      "&:hover": { backgroundColor: "#0D47A1" },
                      textTransform: "none",
                      whiteSpace: "nowrap"
                    }}
                  >
                    Editar límite
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<SaveRoundedIcon />}
                    onClick={guardarLimiteUsuarios}
                    disabled={savingLimit || !String(limitInput || "").trim()}
                    sx={{
                      backgroundColor: "#2E7D32",
                      "&:hover": { backgroundColor: "#1B5E20" },
                      textTransform: "none",
                      whiteSpace: "nowrap"
                    }}
                  >
                    Guardar
                  </Button>
                )}
              </Stack>
            )}
          </Box>
        </Paper>
      </Stack>

      <Button
        variant="contained"
        onClick={abrirNuevoUsuario}
        sx={{
          backgroundColor: "#1565C0",
          "&:hover": { backgroundColor: "#0D47A1" },
          textTransform: "none",
          mb: 3
        }}
      >
        Crear nuevo usuario
      </Button>

      <Paper sx={{ overflow: "hidden", borderRadius: "10px" }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#1565C0" }}>
            <TableRow>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Nombre</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Correo</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Teléfono</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Rol</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Estado</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Clave Temporal</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Acciones</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {usuariosVisibles.map((u) => (
              <TableRow key={u.id}>
                <TableCell>{u.nombre}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.telefono}</TableCell>
                <TableCell>{mostrarRol(u.rol)}</TableCell>

                <TableCell>
                  <Chip
                    label={u.activo ? "Activo" : "Inactivo"}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      bgcolor: u.activo ? "#E8F5E9" : "#FFEBEE",
                      color: u.activo ? "#2E7D32" : "#C62828"
                    }}
                  />
                </TableCell>

                <TableCell>
                  {u.clave_temporal ? (
                    <Chip label="Pendiente" size="small" sx={{ fontWeight: 700 }} color="warning" />
                  ) : (
                    <Chip label="Actualizada" size="small" sx={{ fontWeight: 700 }} />
                  )}
                </TableCell>

                <TableCell>
                  {u.clave_temporal ? (
                    <Tooltip title="Enviar clave temporal">
                      <span>
                        <IconButton
                          onClick={(e) => abrirMenuEnvio(e, u)}
                          sx={{ bgcolor: "#E3F2FD", mr: 1 }}
                          disabled={sendingEmail}
                        >
                          <SendIcon sx={{ color: "#1565C0" }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Generar nueva clave temporal">
                      <IconButton
                        onClick={() => abrirGenerarClave(u)}
                        sx={{ bgcolor: "#F3E5F5", mr: 1 }}
                      >
                        <KeyIcon sx={{ color: "#7B1FA2" }} />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Tooltip title="Editar usuario">
                    <IconButton onClick={() => abrirEditar(u)} sx={{ bgcolor: "#E3F2FD", mr: 1 }}>
                      <EditIcon sx={{ color: "#1565C0" }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Eliminar usuario">
                    <IconButton onClick={() => abrirEliminar(u)} sx={{ bgcolor: "#FFEBEE" }}>
                      <DeleteIcon sx={{ color: "#C62828" }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={cerrarMenu}>
        <MenuItem onClick={() => enviarWhatsApp(usuarioSeleccionado)}>
          <WhatsAppIcon sx={{ color: "green", mr: 1 }} /> WhatsApp
        </MenuItem>
        <MenuItem onClick={() => enviarCorreo(usuarioSeleccionado)} disabled={sendingEmail}>
          <EmailIcon sx={{ color: "#1565C0", mr: 1 }} /> Correo
        </MenuItem>
      </Menu>

      {openNewUser && (
        <NewUser
          key={newUserFormKey}
          open={openNewUser}
          onClose={cerrarNuevoUsuario}
          onCreated={async () => {
            await cargarUsuarios();
            await cargarLimites();
            cerrarNuevoUsuario();
            setSnack({ open: true, msg: "Usuario creado correctamente", type: "success" });
          }}
          canCreateSuperUsers={canCreateSuperUsers}
        />
      )}

      <GenerateTempKey
        open={openGenerate}
        user={usuarioSeleccionado}
        empresaNombre={limits?.empresa_nombre || "FACTUSYSDR"}
        loginUrl={loginUrl || "https://factusysdr.com"}
        onClose={() => setOpenGenerate(false)}
        onDone={async (msg, type = "success") => {
          await cargarUsuarios();
          setSnack({ open: true, msg: msg || "Clave temporal generada", type });
        }}
      />

      <EditDeleteUsers
        open={openEditDelete}
        mode={editDeleteMode}
        user={usuarioSeleccionado}
        onClose={() => setOpenEditDelete(false)}
        onDone={async (msg, type = "success") => {
          await cargarUsuarios();
          await cargarLimites();
          setSnack({ open: true, msg: msg || "Actualizado", type });
        }}
      />

      <Dialog
        open={sendingEmail}
        PaperProps={{
          sx: {
            borderRadius: "18px",
            px: 2,
            py: 1.5,
            minWidth: 280
          }
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            textAlign: "center",
            py: 3
          }}
        >
          <CircularProgress size={34} sx={{ color: "#1565C0" }} />
          <Box>
            <Typography sx={{ fontWeight: 700, color: "#1565C0", mb: 0.5 }}>
              Enviando clave temporal
            </Typography>
            <Typography sx={{ color: "#607D8B", fontSize: "0.95rem" }}>
              Por favor espera un momento...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snack.type} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EnterpriseUsers;